import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Radio, Loader2, Send, Users, MapPin, Star, Bike, Search, Zap, CheckCircle2, Clock, X } from "lucide-react";

export default function AdminTargetBroadcast() {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filter, setFilter] = useState({
    city: "",
    vehicle_type: "any",
    min_rating: "",
    online_only: true,
    agent_ids: [],
    note: "",
  });
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [activeBroadcasts, setActiveBroadcasts] = useState([]);

  const fetchPendingOrders = async () => {
    setLoadingOrders(true);
    try {
      const { data } = await api.get("/orders/all?status=pending");
      setPendingOrders(Array.isArray(data) ? data.slice(0, 50) : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load orders");
    } finally { setLoadingOrders(false); }
  };

  const fetchActiveBroadcasts = async () => {
    try {
      const { data } = await api.get("/dispatch/admin/broadcasts");
      setActiveBroadcasts(data.broadcasts || []);
    } catch { /* ignore */ }
  };

  const fetchEligibleAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const params = new URLSearchParams();
      if (filter.city.trim()) params.set("city", filter.city.trim());
      if (filter.vehicle_type && filter.vehicle_type !== "any") params.set("vehicle_type", filter.vehicle_type);
      if (filter.min_rating) params.set("min_rating", filter.min_rating);
      params.set("online_only", String(filter.online_only));
      const { data } = await api.get(`/dispatch/admin/eligible-agents?${params.toString()}`);
      setAgents(data.agents || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load agents");
    } finally { setLoadingAgents(false); }
  }, [filter.city, filter.vehicle_type, filter.min_rating, filter.online_only]);

  useEffect(() => { fetchPendingOrders(); fetchActiveBroadcasts(); }, []);
  useEffect(() => { fetchEligibleAgents(); }, [fetchEligibleAgents]);

  const toggleAgent = (id) => setFilter((f) => {
    const set = new Set(f.agent_ids);
    if (set.has(id)) set.delete(id); else set.add(id);
    return { ...f, agent_ids: [...set] };
  });

  const broadcast = async () => {
    if (!selectedOrder) return toast.error("Pick an order first");
    const payload = {
      tracking_id: selectedOrder.tracking_id,
      agent_ids: filter.agent_ids.length > 0 ? filter.agent_ids : null,
      city: filter.city.trim() || null,
      vehicle_type: filter.vehicle_type && filter.vehicle_type !== "any" ? filter.vehicle_type : null,
      min_rating: filter.min_rating ? parseFloat(filter.min_rating) : null,
      require_online: filter.online_only,
      require_verified: true,
      note: filter.note.trim() || null,
    };
    if (!payload.agent_ids && !payload.city && !payload.vehicle_type && payload.min_rating === null) {
      return toast.error("Pick at least one filter (agents, city, vehicle, or min rating)");
    }
    setBroadcasting(true);
    try {
      const { data } = await api.post("/dispatch/admin/target-broadcast", payload);
      toast.success(`Broadcast sent to ${data.match_count} agent${data.match_count === 1 ? "" : "s"}`);
      setSelectedOrder(null);
      setFilter((f) => ({ ...f, agent_ids: [], note: "" }));
      fetchPendingOrders();
      fetchActiveBroadcasts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Broadcast failed");
    } finally { setBroadcasting(false); }
  };

  const matchedAgents = filter.agent_ids.length > 0 ? agents.filter((a) => filter.agent_ids.includes(a._id)) : agents;

  return (
    <div className="space-y-6 animate-fadeInUp" data-testid="admin-target-broadcast-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Radio className="h-7 w-7 text-[#FF6B00]" /> Target Broadcast
          </h1>
          <p className="text-sm text-slate-500 mt-1">Re-broadcast a pending order to a specific set of agents — by city, vehicle, rating or hand-picked IDs.</p>
        </div>
        <Button onClick={broadcast} disabled={broadcasting || !selectedOrder} className="bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11 px-5" data-testid="broadcast-btn">
          {broadcasting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Send Broadcast</>}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT: pick order */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="pending-orders-panel">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-slate-400" />
            <span className="font-['Outfit'] font-bold text-slate-900">Pending Orders</span>
            <span className="ml-auto text-xs text-slate-400">{pendingOrders.length}</span>
          </div>
          {loadingOrders ? (
            <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>
          ) : pendingOrders.length === 0 ? (
            <div className="py-10 text-center text-xs text-slate-400">No pending orders to broadcast.</div>
          ) : (
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {pendingOrders.map((o) => (
                <button
                  key={o.tracking_id}
                  onClick={() => setSelectedOrder(o)}
                  className={`w-full text-left rounded-xl border p-3 transition ${
                    selectedOrder?.tracking_id === o.tracking_id
                      ? "border-[#FF6B00] bg-orange-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`pending-order-${o.tracking_id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] font-bold text-[#FF6B00]">{o.tracking_id}</span>
                    <span className="text-[10px] text-slate-400">{o.order_type || "courier"}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1 line-clamp-1">→ {o.pickup_address}</div>
                  <div className="text-xs text-slate-500 line-clamp-1">⤷ {o.delivery_address}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* MIDDLE: filters */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="target-filters-panel">
          <div className="font-['Outfit'] font-bold text-slate-900 mb-4">Target Filters</div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> City / area keyword</Label>
              <Input
                placeholder="e.g. Koramangala, HSR, Bangalore"
                value={filter.city}
                onChange={(e) => setFilter((f) => ({ ...f, city: e.target.value }))}
                className="rounded-xl h-10 mt-1.5" data-testid="filter-city-input"
              />
              <p className="text-[10px] text-slate-400 mt-1">Matched against the agent's saved address (case-insensitive).</p>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><Bike className="h-3.5 w-3.5" /> Vehicle type</Label>
              <Select value={filter.vehicle_type} onValueChange={(v) => setFilter((f) => ({ ...f, vehicle_type: v }))}>
                <SelectTrigger className="rounded-xl h-10 mt-1.5" data-testid="filter-vehicle-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any vehicle</SelectItem>
                  <SelectItem value="bike">Bike</SelectItem>
                  <SelectItem value="scooter">Scooter</SelectItem>
                  <SelectItem value="car">Car</SelectItem>
                  <SelectItem value="van">Van</SelectItem>
                  <SelectItem value="truck">Truck</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><Star className="h-3.5 w-3.5" /> Minimum rating</Label>
              <Input
                type="number" step="0.1" min="0" max="5" placeholder="e.g. 4.2"
                value={filter.min_rating}
                onChange={(e) => setFilter((f) => ({ ...f, min_rating: e.target.value }))}
                className="rounded-xl h-10 mt-1.5" data-testid="filter-rating-input"
              />
            </div>

            <div className="flex items-center justify-between px-3 py-2.5 bg-slate-50 rounded-xl">
              <div>
                <div className="text-sm font-semibold text-slate-900">Online agents only</div>
                <div className="text-[11px] text-slate-500">Skip agents who aren't online right now</div>
              </div>
              <Switch checked={filter.online_only} onCheckedChange={(v) => setFilter((f) => ({ ...f, online_only: v }))} data-testid="filter-online-toggle" />
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700">Note to agent (optional)</Label>
              <Input
                placeholder="e.g. VIP customer — be on time"
                value={filter.note}
                onChange={(e) => setFilter((f) => ({ ...f, note: e.target.value }))}
                className="rounded-xl h-10 mt-1.5" data-testid="filter-note-input"
              />
            </div>

            {selectedOrder ? (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 flex items-center gap-2" data-testid="selected-order-badge">
                <CheckCircle2 className="h-4 w-4" />
                <span>Target order: <span className="font-mono font-bold">{selectedOrder.tracking_id}</span></span>
                <button onClick={() => setSelectedOrder(null)} className="ml-auto"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                Pick a pending order from the left first.
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: eligible agents */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="eligible-agents-panel">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-slate-400" />
            <span className="font-['Outfit'] font-bold text-slate-900">Matching Agents</span>
            <span className="ml-auto inline-flex items-center gap-1 bg-[#FF6B00]/10 text-[#FF6B00] text-[11px] font-bold px-2 py-0.5 rounded-full" data-testid="matching-agent-count">
              <Zap className="h-3 w-3" /> {matchedAgents.length} / {agents.length}
            </span>
          </div>
          <p className="text-[11px] text-slate-400 mb-3">
            {filter.agent_ids.length > 0
              ? `${filter.agent_ids.length} hand-picked. Broadcast will go to these only.`
              : "Broadcast will go to everyone in this list."}
          </p>
          {loadingAgents ? (
            <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>
          ) : agents.length === 0 ? (
            <div className="py-10 text-center text-xs text-slate-400 flex flex-col items-center gap-2">
              <Search className="h-6 w-6" />
              No agents match these filters.
            </div>
          ) : (
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {agents.map((a) => {
                const picked = filter.agent_ids.includes(a._id);
                return (
                  <button
                    key={a._id}
                    onClick={() => toggleAgent(a._id)}
                    className={`w-full text-left rounded-xl border p-3 transition ${
                      picked ? "border-[#FF6B00] bg-orange-50" : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`eligible-agent-${a._id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-900">{a.name}</span>
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-600">
                        <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {a.rating.toFixed(1)}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">
                      {a.vehicle_type || "—"} · {a.address || "No address"}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      {a.is_online ? (
                        <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">ONLINE</span>
                      ) : (
                        <span className="text-[9px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">OFFLINE</span>
                      )}
                      {picked && <span className="text-[9px] font-bold text-[#FF6B00] bg-[#FF6B00]/10 px-1.5 py-0.5 rounded">PICKED</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Active broadcasts */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="active-broadcasts-panel">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="h-4 w-4 text-[#FF6B00]" />
          <span className="font-['Outfit'] font-bold text-slate-900">Live Broadcasts</span>
          <span className="ml-auto text-xs text-slate-400">{activeBroadcasts.length}</span>
        </div>
        {activeBroadcasts.length === 0 ? (
          <div className="py-8 text-center text-xs text-slate-400">Nothing live right now.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {activeBroadcasts.map((b) => (
              <div key={b.tracking_id} className="rounded-xl border border-slate-200 p-3" data-testid={`live-broadcast-${b.tracking_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-[11px] font-bold text-[#FF6B00]">{b.tracking_id}</span>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${b.status === "accepted" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {b.status.toUpperCase()}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 line-clamp-1">→ {b.pickup_address}</div>
                {b.target_filter && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {b.target_filter.city && <span className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">📍 {b.target_filter.city}</span>}
                    {b.target_filter.vehicle_type && <span className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">🛵 {b.target_filter.vehicle_type}</span>}
                    {b.target_filter.min_rating && <span className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">⭐ ≥{b.target_filter.min_rating}</span>}
                    {(b.target_filter.agent_ids || []).length > 0 && <span className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">🎯 {b.target_filter.agent_ids.length} agents</span>}
                  </div>
                )}
                {b.accepted_by_name && <div className="text-[10px] text-emerald-700 mt-1.5">Accepted by <b>{b.accepted_by_name}</b></div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
